/**
 * DashaPage — Vimshottari Dasha timeline with plain-language interpretations.
 *   - Current Mahadasha/Antardasha with planet icons + date ranges
 *   - Plain-language domain cards (career, relationships, health, finances, spirituality)
 *   - Transition alert banner (upcoming period changes)
 *   - 12-month upcoming antardasha transitions timeline
 *   - 120-year overview ruler
 *   - Mahadasha list with Antardasha → Pratyantardasha drill-down
 *   - EN/HI toggle, loading skeletons
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  dashaApi,
  chartApi,
  isNotFoundError,
  type DashaNode,
  type CurrentDasha,
  type VedAstroPlanetEnrichment,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { useFeatureGate } from '@/hooks/useSubscription'
import { SoftGate } from '@/components/paywall'
import { ChevronDown, ChevronRight, Clock, BookOpen, AlertTriangle } from 'lucide-react'
import dayjs from 'dayjs'
import { ErrorState, SetupPrompt, SkeletonDashboard } from '@/components/ui'
import { Card } from '@/components/Card'

// ── Constants ────────────────────────────────────────────────────────────────

const PLANET_ORDER = ['Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury'] as const

const PLANET_YEARS: Record<string, number> = {
  Ketu: 7, Venus: 20, Sun: 6, Moon: 10, Mars: 7,
  Rahu: 18, Jupiter: 16, Saturn: 19, Mercury: 17,
}

const PLANET_ICONS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋',
}

const PLANET_COLORS: Record<string, { bg: string; text: string; border: string; glow: string }> = {
  Sun:     { bg: 'bg-amber-500/20',  text: 'text-amber-400',   border: 'border-amber-500/30',  glow: 'shadow-[0_0_12px_rgba(245,158,11,0.3)]' },
  Moon:    { bg: 'bg-star/10',       text: 'text-star',        border: 'border-star/30',        glow: 'shadow-[0_0_12px_rgba(200,200,240,0.3)]' },
  Mars:    { bg: 'bg-red-500/20',    text: 'text-red-400',     border: 'border-red-500/30',     glow: 'shadow-[0_0_12px_rgba(239,68,68,0.3)]' },
  Mercury: { bg: 'bg-green-500/20',  text: 'text-green-400',   border: 'border-green-500/30',   glow: 'shadow-[0_0_12px_rgba(34,197,94,0.3)]' },
  Jupiter: { bg: 'bg-yellow-500/20', text: 'text-yellow-400',  border: 'border-yellow-500/30',  glow: 'shadow-[0_0_12px_rgba(234,179,8,0.3)]' },
  Venus:   { bg: 'bg-pink-500/20',   text: 'text-pink-400',    border: 'border-pink-500/30',    glow: 'shadow-[0_0_12px_rgba(244,114,182,0.3)]' },
  Saturn:  { bg: 'bg-slate-500/20',  text: 'text-slate-400',   border: 'border-slate-400/30',   glow: 'shadow-[0_0_12px_rgba(148,163,184,0.25)]' },
  Rahu:    { bg: 'bg-violet/20',     text: 'text-violet-glow', border: 'border-violet/30',      glow: 'shadow-[0_0_12px_rgba(167,139,250,0.3)]' },
  Ketu:    { bg: 'bg-orange-500/20', text: 'text-orange-400',  border: 'border-orange-500/30',  glow: 'shadow-[0_0_12px_rgba(251,146,60,0.3)]' },
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isActive(node: DashaNode): boolean {
  const now = dayjs()
  return now.isAfter(dayjs(node.startDate)) && now.isBefore(dayjs(node.endDate))
}

function progressPercent(node: DashaNode): number {
  const now = dayjs()
  const total = dayjs(node.endDate).diff(dayjs(node.startDate), 'day')
  const elapsed = now.diff(dayjs(node.startDate), 'day')
  return Math.max(0, Math.min(100, (elapsed / total) * 100))
}

function formatDateRange(start: string, end: string): string {
  return `${dayjs(start).format('MMM YYYY')} – ${dayjs(end).format('MMM YYYY')}`
}

function remainingLabel(endDate: string): string {
  const now = dayjs()
  const end = dayjs(endDate)
  if (end.isBefore(now)) return 'Completed'
  const months = end.diff(now, 'month')
  if (months < 1) return `${end.diff(now, 'day')}d remaining`
  if (months < 24) return `${months}mo remaining`
  return `${end.diff(now, 'year')}yr remaining`
}

/** Compute sub-periods proportionally (antardasha or pratyantardasha). */
function computeSubPeriods(
  startDate: string,
  durationDays: number,
  startPlanetIdx: number,
  level: DashaNode['level'],
): DashaNode[] {
  let cursor = dayjs(startDate)
  return PLANET_ORDER.map((_, i) => {
    const planet = PLANET_ORDER[(startPlanetIdx + i) % 9]
    const subDays = (PLANET_YEARS[planet] / 120) * durationDays
    const nodeStart = cursor.format('YYYY-MM-DD')
    const nodeEnd = cursor.add(subDays, 'day').format('YYYY-MM-DD')
    cursor = cursor.add(subDays, 'day')
    return { planet, startDate: nodeStart, endDate: nodeEnd, level }
  })
}

// ── (Inline DashaSkeleton removed — uses SkeletonDashboard from ui.tsx) ───────

// ── Transition alert banner ───────────────────────────────────────────────────

function TransitionBanner({
  current,
  lang,
}: {
  current: CurrentDasha
  lang: 'en' | 'hi'
}) {
  const { transitionAlert, currentAntardasha } = current
  const daysLeft = transitionAlert.antardashaEndsInDays

  // Only show when transition is within 90 days
  if (daysLeft > 90) return null

  const nextPlanetIdx = currentAntardasha
    ? (PLANET_ORDER.indexOf(currentAntardasha.planet as typeof PLANET_ORDER[number]) + 1) % 9
    : 0
  const nextPlanet = PLANET_ORDER[nextPlanetIdx]

  return (
    <div
      role="alert"
      className="p-4 rounded-xl bg-amber-500/8 border border-amber-500/25 space-y-2"
      aria-label={`Transition alert: ${daysLeft} days until next period`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" aria-hidden="true" />
        <p className="text-amber-400 text-sm font-semibold">
          {PLANET_ICONS[nextPlanet] ?? ''} {nextPlanet} Antardasha begins in{' '}
          <span className="font-mono">{daysLeft}</span> days
        </p>
      </div>
      <p className={cn('text-sm leading-relaxed text-star-soft', lang === 'hi' ? 'font-display' : '')}>
        {transitionAlert.whatToExpect[lang]}
      </p>
    </div>
  )
}

// ── Upcoming 12-month transitions ─────────────────────────────────────────────

interface TransitionEvent {
  planet: string
  startDate: dayjs.Dayjs
  label: string
  type: 'antardasha' | 'mahadasha'
}

function UpcomingTransitions({ dashas }: { dashas: DashaNode[] }) {
  const now = dayjs()
  const cutoff = now.add(12, 'month')

  const events: TransitionEvent[] = []

  for (const maha of dashas) {
    const mahaStart = dayjs(maha.startDate)
    if (mahaStart.isAfter(now) && mahaStart.isBefore(cutoff)) {
      events.push({ planet: maha.planet, startDate: mahaStart, label: `${maha.planet} Mahadasha`, type: 'mahadasha' })
    }
    for (const antar of maha.children ?? []) {
      const antarStart = dayjs(antar.startDate)
      if (antarStart.isAfter(now) && antarStart.isBefore(cutoff)) {
        events.push({ planet: antar.planet, startDate: antarStart, label: `${antar.planet} Antardasha`, type: 'antardasha' })
      }
    }
  }

  events.sort((a, b) => a.startDate.unix() - b.startDate.unix())

  if (events.length === 0) {
    return (
      <p className="text-star-muted text-sm text-center py-4">
        No period transitions in the next 12 months.
      </p>
    )
  }

  const totalDays = 365

  return (
    <div className="space-y-3">
      {/* Horizontal timeline strip */}
      <div className="relative h-8 rounded-lg overflow-hidden bg-zinc-900/60" aria-hidden="true">
        {events.map((ev, i) => {
          const pct = (now.diff(ev.startDate, 'day') / -totalDays) * 100
          const colors = PLANET_COLORS[ev.planet] ?? PLANET_COLORS.Saturn
          return (
            <div
              key={i}
              className={cn('absolute top-0 bottom-0 w-1.5 rounded-sm', colors.text.replace('text-', 'bg-'), 'opacity-80')}
              style={{ left: `${Math.min(97, pct)}%` }}
              title={`${ev.label} — ${ev.startDate.format('MMM YYYY')}`}
            />
          )
        })}
        {/* Today marker */}
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gold opacity-70" />
        <span className="absolute left-1 top-1/2 -translate-y-1/2 text-2xs font-mono text-gold">Today</span>
        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-2xs font-mono text-star-dim">+12 mo</span>
      </div>

      {/* Event list */}
      <ul className="space-y-1.5" role="list" aria-label="Upcoming period transitions">
        {events.map((ev, i) => {
          const colors = PLANET_COLORS[ev.planet] ?? PLANET_COLORS.Saturn
          const daysUntil = ev.startDate.diff(now, 'day')
          return (
            <li
              key={i}
              className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg border', colors.bg, colors.border)}
            >
              <span className={cn('text-base leading-none flex-shrink-0', colors.text)} aria-hidden="true">
                {PLANET_ICONS[ev.planet] ?? ''}
              </span>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-semibold', colors.text)}>{ev.label}</p>
                <p className="text-star-dim text-2xs font-mono">{ev.startDate.format('D MMM YYYY')}</p>
              </div>
              <span className={cn('text-2xs font-mono flex-shrink-0 px-2 py-0.5 rounded border', colors.bg, colors.border, colors.text)}>
                {daysUntil}d
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ── Pratyantardasha rows ──────────────────────────────────────────────────────

function PratyantardashaList({ antarNode }: { antarNode: DashaNode }) {
  const duration = dayjs(antarNode.endDate).diff(dayjs(antarNode.startDate), 'day')
  const startIdx = PLANET_ORDER.indexOf(antarNode.planet as typeof PLANET_ORDER[number])
  const pratys = computeSubPeriods(antarNode.startDate, duration, startIdx, 'pratyantardasha')
  const now = dayjs()

  return (
    <div className="ml-4 mt-1.5 space-y-1" role="list" aria-label={`${antarNode.planet} Pratyantardasha sub-periods`}>
      <p className="text-star-dim text-2xs font-mono uppercase tracking-widest px-2 mb-1.5">
        Pratyantardashas
      </p>
      {pratys.map(praty => {
        const active = now.isAfter(dayjs(praty.startDate)) && now.isBefore(dayjs(praty.endDate))
        const past = dayjs(praty.endDate).isBefore(now)
        const colors = PLANET_COLORS[praty.planet] ?? PLANET_COLORS.Saturn
        return (
          <div
            key={praty.planet}
            role="listitem"
            className={cn(
              'flex items-center gap-2.5 px-3 py-1.5 rounded-md border text-xs transition-all',
              active ? cn(colors.bg, colors.border) : past ? 'bg-zinc-950/40 border-zinc-800/30 opacity-50' : 'bg-zinc-900/30 border-zinc-800/40',
            )}
          >
            <span className={cn('text-sm leading-none flex-shrink-0', active ? colors.text : 'text-star-dim')} aria-hidden="true">
              {PLANET_ICONS[praty.planet] ?? ''}
            </span>
            <span className={cn('font-medium', active ? colors.text : 'text-star-muted')}>{praty.planet}</span>
            {active && (
              <span className={cn('text-2xs font-mono px-1 py-0.5 rounded border ml-0.5', colors.bg, colors.border, colors.text)}>
                Active
              </span>
            )}
            <span className="text-star-dim font-mono text-2xs ml-auto">
              {dayjs(praty.startDate).format('MMM YY')} – {dayjs(praty.endDate).format('MMM YY')}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ── Antardasha row ────────────────────────────────────────────────────────────

function AntardashaRow({ node }: { node: DashaNode }) {
  const [showPratys, setShowPratys] = useState(false)
  const active = isActive(node)
  const colors = PLANET_COLORS[node.planet] ?? PLANET_COLORS.Saturn
  const pct = active ? progressPercent(node) : 0

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all',
          active
            ? cn(colors.bg, colors.border, colors.glow)
            : 'bg-zinc-900/40 border-zinc-800/50',
        )}
      >
        {/* Active pulse dot */}
        <div className="relative flex-shrink-0">
          <div className={cn('w-2 h-2 rounded-full', active ? colors.text.replace('text-', 'bg-') : 'bg-zinc-700')} />
          {active && (
            <div className={cn('absolute inset-0 rounded-full animate-ping opacity-75', colors.text.replace('text-', 'bg-'))} />
          )}
        </div>

        {/* Planet icon + name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span className={cn('text-sm leading-none', active ? colors.text : 'text-star-dim')} aria-hidden="true">
              {PLANET_ICONS[node.planet] ?? ''}
            </span>
            <span className={cn('text-sm font-semibold', active ? colors.text : 'text-star-soft')}>
              {node.planet}
            </span>
            {active && (
              <span className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', colors.bg, colors.border, colors.text)}>
                Active
              </span>
            )}
          </div>
          <p className="text-star-dim text-2xs font-mono">{formatDateRange(node.startDate, node.endDate)}</p>
        </div>

        {active && (
          <div className="flex-shrink-0 text-right">
            <p className={cn('text-2xs font-mono', colors.text)}>{remainingLabel(node.endDate)}</p>
            <div className="mt-1 w-20 h-1 rounded-full bg-zinc-800 overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', colors.text.replace('text-', 'bg-'))}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* Pratyantardasha toggle */}
        <button
          onClick={() => setShowPratys(v => !v)}
          aria-expanded={showPratys}
          aria-label={`${showPratys ? 'Hide' : 'Show'} ${node.planet} Pratyantardasha sub-periods`}
          className="flex-shrink-0 p-1 text-star-dim hover:text-star-bright transition-colors"
        >
          {showPratys ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
      </div>

      {showPratys && <PratyantardashaList antarNode={node} />}
    </div>
  )
}

// ── Mahadasha card ────────────────────────────────────────────────────────────

function DashaStrengthBadge({ e }: { e: VedAstroPlanetEnrichment }) {
  const score = e.ashtakavargaScore
  const color = score >= 5 ? 'text-auspicious border-auspicious/30 bg-auspicious/10'
    : score >= 3 ? 'text-gold border-gold/30 bg-gold/10'
    : 'text-inauspicious border-inauspicious/30 bg-inauspicious/10'

  let dignityShort = ''
  if (e.isExalted)          dignityShort = 'Exalted'
  else if (e.isInOwnSign)   dignityShort = 'Own'
  else if (e.isDebilitated) dignityShort = 'Debil.'

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {dignityShort && (
        <span className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', color)}>
          {dignityShort}
        </span>
      )}
      <span
        className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', color)}
        title={`Ashtakavarga: ${score}/8`}
      >
        AV {score}/8
      </span>
    </div>
  )
}

function MahadashaCard({ node, planetEnrichment }: { node: DashaNode; planetEnrichment?: VedAstroPlanetEnrichment | null }) {
  const [expanded, setExpanded] = useState(isActive(node))
  const active = isActive(node)
  const colors = PLANET_COLORS[node.planet] ?? PLANET_COLORS.Saturn
  const durationYears = PLANET_YEARS[node.planet]
  const pct = active ? progressPercent(node) : 0

  return (
    <div className={cn('space-y-2', active && 'animate-fade-in')}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        aria-controls={`antar-${node.planet}`}
        className={cn(
          'w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-all duration-200',
          active
            ? cn(colors.bg, colors.border, colors.glow)
            : 'bg-zinc-900/50 border-zinc-800/50 hover:bg-zinc-800/60 hover:border-zinc-700/60',
        )}
      >
        {/* Planet icon + pulse dot */}
        <div className="relative flex-shrink-0 flex items-center gap-1.5">
          <span className={cn('text-xl leading-none', active ? colors.text : 'text-star-dim')} aria-hidden="true">
            {PLANET_ICONS[node.planet] ?? ''}
          </span>
          <div className={cn('w-2.5 h-2.5 rounded-full', active ? colors.text.replace('text-', 'bg-') : 'bg-zinc-700')} />
          {active && (
            <div className={cn('absolute right-0 w-2.5 h-2.5 rounded-full animate-pulse-gold opacity-60', colors.text.replace('text-', 'bg-'))} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={cn('text-base font-bold', active ? colors.text : 'text-star-bright')}>
              {node.planet} Mahadasha
            </span>
            <span className="text-star-dim text-2xs font-mono">{durationYears} years</span>
            {active && (
              <span className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', colors.bg, colors.border, colors.text)}>
                ● Current
              </span>
            )}
          </div>
          <p className="text-star-muted text-xs font-mono mt-0.5">
            {formatDateRange(node.startDate, node.endDate)}
          </p>
          {active && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
                <div
                  className={cn('h-full rounded-full transition-all', colors.text.replace('text-', 'bg-'))}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className={cn('text-2xs font-mono flex-shrink-0', colors.text)}>
                {remainingLabel(node.endDate)}
              </span>
            </div>
          )}
        </div>

        {/* VedAstro enrichment strength badge */}
        {planetEnrichment && <DashaStrengthBadge e={planetEnrichment} />}

        <div className="flex-shrink-0 text-star-dim" aria-hidden="true">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>

      {expanded && node.children && (
        <div id={`antar-${node.planet}`} className="ml-6 space-y-1.5 animate-fade-in">
          <p className="text-star-dim text-2xs font-mono uppercase tracking-widest px-2 mb-2">
            Antardashas — click ▶ to see Pratyantardashas
          </p>
          {node.children.map(child => (
            <AntardashaRow key={child.planet} node={child} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Current dasha interpretation ──────────────────────────────────────────────

const DOMAIN_LABELS: Array<{
  key: keyof Omit<CurrentDasha['currentMahadasha']['interpretation'], 'dignityNote' | 'houseContext'>
  label: string
  icon: string
}> = [
  { key: 'overallTheme',  label: 'Overall Theme',  icon: '✦' },
  { key: 'career',        label: 'Career',          icon: '⚡' },
  { key: 'relationships', label: 'Relationships',   icon: '♡' },
  { key: 'health',        label: 'Health',          icon: '○' },
  { key: 'finances',      label: 'Finances',        icon: '◈' },
  { key: 'spirituality',  label: 'Spirituality',    icon: '✧' },
]

function CurrentDashaInterpretation({
  current,
  lang,
  setLang,
}: {
  current: CurrentDasha
  lang: 'en' | 'hi'
  setLang: (l: 'en' | 'hi') => void
}) {
  const [expandedDomain, setExpandedDomain] = useState<string | null>('overallTheme')

  const { currentMahadasha: maha, currentAntardasha: antar } = current
  const t = (b: { en: string; hi: string }) => b[lang]
  const mahaColors = PLANET_COLORS[maha.planet] ?? PLANET_COLORS.Saturn

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-gold" aria-hidden="true" />
          <h2 className="text-star-bright text-sm font-semibold">Period Interpretation</h2>
        </div>
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-cosmic-dusk ring-1 ring-white/5" role="group" aria-label="Select language">
          {(['en', 'hi'] as const).map(l => (
            <button
              key={l}
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              className={cn(
                'px-2.5 py-1 rounded-md text-2xs font-mono transition-all duration-fast',
                lang === l
                  ? 'bg-violet/20 text-violet-glow shadow-card'
                  : 'text-star-dim hover:text-star-bright',
              )}
            >
              {l === 'en' ? 'English' : 'हिंदी'}
            </button>
          ))}
        </div>
      </div>

      {/* Active period header */}
      <div className={cn('p-4 rounded-xl border space-y-1', mahaColors.bg, mahaColors.border)}>
        <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Active Period</p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('text-xl leading-none', mahaColors.text)} aria-hidden="true">
            {PLANET_ICONS[maha.planet] ?? ''}
          </span>
          <span className={cn('text-base font-bold', mahaColors.text)}>{maha.planet} Mahadasha</span>
          {antar && (
            <>
              <span className="text-star-dim">·</span>
              <span className={cn('text-sm leading-none', PLANET_COLORS[antar.planet]?.text ?? 'text-star-soft')} aria-hidden="true">
                {PLANET_ICONS[antar.planet] ?? ''}
              </span>
              <span className={cn('text-sm font-medium', PLANET_COLORS[antar.planet]?.text ?? 'text-star-soft')}>
                {antar.planet} Antardasha
              </span>
            </>
          )}
        </div>
        {maha.interpretation.dignityNote && (
          <p className={cn('text-xs mt-1', lang === 'hi' ? 'font-display' : '', mahaColors.text, 'opacity-80')}>
            {t(maha.interpretation.dignityNote)}
          </p>
        )}
        {maha.interpretation.houseContext && (
          <p className={cn('text-xs', lang === 'hi' ? 'font-display' : '', 'text-star-dim')}>
            {t(maha.interpretation.houseContext)}
          </p>
        )}
      </div>

      {/* Six-domain accordion */}
      <div className="space-y-1.5" role="list" aria-label="Life domain interpretations">
        {DOMAIN_LABELS.map(({ key, label, icon }) => {
          const text = maha.interpretation[key]
          const isOpen = expandedDomain === key
          return (
            <div key={key} role="listitem" className="rounded-lg bg-zinc-900/50 border border-zinc-800/50 overflow-hidden">
              <button
                onClick={() => setExpandedDomain(isOpen ? null : key)}
                aria-expanded={isOpen}
                aria-controls={`domain-${key}`}
                className="w-full flex items-center gap-2 justify-between px-4 py-2.5 text-left hover:bg-zinc-800/50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="text-gold text-sm font-mono w-4" aria-hidden="true">{icon}</span>
                  <span className="text-star-bright text-sm font-medium">{label}</span>
                </span>
                {isOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-star-dim flex-shrink-0" aria-hidden="true" />
                  : <ChevronRight className="w-3.5 h-3.5 text-star-dim flex-shrink-0" aria-hidden="true" />
                }
              </button>
              {isOpen && (
                <div id={`domain-${key}`} className="px-4 pb-3 border-t border-zinc-800/50">
                  <p className={cn('text-sm leading-relaxed mt-2', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-soft')}>
                    {t(text)}
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Timeline ruler ────────────────────────────────────────────────────────────

function TimelineRuler({
  dashas,
  minDate,
  totalSpan,
}: {
  dashas: DashaNode[]
  minDate: dayjs.Dayjs
  totalSpan: number
}) {
  const now = dayjs()
  const nowOffset = (now.diff(minDate, 'day') / totalSpan) * 100

  return (
    <div className="relative h-10 mb-2 overflow-hidden rounded-lg" aria-hidden="true">
      <div className="absolute inset-0 bg-zinc-900/60 rounded-lg" />

      {dashas.map(node => {
        const offsetDays = dayjs(node.startDate).diff(minDate, 'day')
        const spanDays   = dayjs(node.endDate).diff(dayjs(node.startDate), 'day')
        const offsetPct  = (offsetDays / totalSpan) * 100
        const widthPct   = (spanDays   / totalSpan) * 100
        const active     = isActive(node)
        const colors     = PLANET_COLORS[node.planet] ?? PLANET_COLORS.Saturn

        return (
          <div
            key={node.planet}
            className={cn(
              'absolute top-0 bottom-0 border-r border-zinc-950/50 flex items-center justify-center transition-opacity',
              colors.bg,
              active ? 'opacity-100' : 'opacity-60',
            )}
            style={{ left: `${offsetPct}%`, width: `${widthPct}%` }}
            title={`${node.planet}: ${formatDateRange(node.startDate, node.endDate)}`}
          >
            <span className={cn('text-2xs font-mono truncate px-1', colors.text)}>
              {PLANET_ICONS[node.planet] ?? node.planet.slice(0, 2)}
            </span>
          </div>
        )
      })}

      {nowOffset >= 0 && nowOffset <= 100 && (
        <div className="absolute top-0 bottom-0 w-0.5 bg-gold z-10" style={{ left: `${nowOffset}%` }}>
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gold rounded-full" />
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-2xs font-mono text-gold whitespace-nowrap">Now</div>
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DashaPage() {
  const dashaGate = useFeatureGate('dasha_full')
  const [lang, setLang] = useState<'en' | 'hi'>('en')

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashas'],
    queryFn: () => dashaApi.getMyDashas(),
    retry: 1,
  })

  const { data: chartData } = useQuery({
    queryKey: ['birth-chart'],
    queryFn: () => chartApi.getMyChart(),
    retry: 1,
  })

  const { data: currentDasha, isLoading: currentLoading } = useQuery({
    queryKey: ['dasha-current', chartData?.id],
    queryFn: () => dashaApi.getCurrentDasha(chartData!.id),
    enabled: !!chartData?.id,
    staleTime: 1000 * 60 * 30, // 30min — current period is time-sensitive
    retry: 1,
  })

  const { data: enrichment } = useQuery({
    queryKey: ['chart-enrichment', chartData?.id],
    queryFn: () => chartApi.getEnrichment(chartData!.id),
    enabled: !!chartData?.id,
    staleTime: 1000 * 60 * 60 * 24, // 24h — enrichment is stable
    retry: false,
  })

  const enrichMap = new Map<string, VedAstroPlanetEnrichment>(
    (enrichment?.planets ?? []).map((e: VedAstroPlanetEnrichment) => [e.graha, e]),
  )

  if (isLoading) return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="heading-cosmic text-xl font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5 text-gold" aria-hidden="true" />
          Dasha Timeline
        </h1>
        <p className="text-star-muted text-sm mt-0.5 font-mono">Vimshottari · 120-year span</p>
      </div>
      <SkeletonDashboard />
    </div>
  )

  if (!data) {
    if (isNotFoundError(error)) return <SetupPrompt message="Set up your birth details to view your Dasha timeline." />
    return <ErrorState message="Unable to load dasha timeline. Please try again." />
  }
  if (!data.length) return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="text-star-white text-xl font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5 text-gold" aria-hidden="true" />
          Dasha Timeline
        </h1>
        <p className="text-star-muted text-sm mt-0.5 font-mono">Vimshottari · 120-year span</p>
      </div>
      <div className="card-cosmic p-8 text-center">
        <p className="text-gold text-lg mb-2">✦</p>
        <p className="text-star-bright font-medium mb-1">Timeline being computed</p>
        <p className="text-star-muted text-sm">Your Dasha timeline is being calculated. Please check back shortly.</p>
      </div>
    </div>
  )

  const dashas = data
  const current = currentDasha

  const minDate  = dayjs(dashas[0].startDate)
  const maxDate  = dayjs(dashas[dashas.length - 1].endDate)
  const totalSpan = maxDate.diff(minDate, 'day')

  const activeMaha  = dashas.find(isActive)
  const activeAntar = activeMaha?.children?.find(isActive)

  return (
    <main className="max-w-4xl space-y-5" aria-label="Dasha Timeline">

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="heading-cosmic text-xl font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-gold" aria-hidden="true" />
            Dasha Timeline
          </h1>
          <p className="text-star-muted text-sm mt-0.5 font-mono">Vimshottari · 120-year span</p>
        </div>
      </div>

      {/* Transition alert banner (appears only when within 90 days) */}
      {current && <TransitionBanner current={current} lang={lang} />}

      {/* Active period summary cards */}
      {activeMaha && (
        <section aria-labelledby="active-dashas-heading">
          <h2 id="active-dashas-heading" className="sr-only">Active Dasha Periods</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {/* Mahadasha card */}
            <Card className={cn('p-5 border', PLANET_COLORS[activeMaha.planet]?.border ?? 'border-cosmic-mist', PLANET_COLORS[activeMaha.planet]?.glow ?? '')}>
              <p className="overline mb-2">Active Mahadasha</p>
              <div className="flex items-center gap-3">
                <span className={cn('text-3xl leading-none', PLANET_COLORS[activeMaha.planet]?.text ?? 'text-gold')} aria-hidden="true">
                  {PLANET_ICONS[activeMaha.planet] ?? ''}
                </span>
                <div>
                  <p className={cn('text-lg font-bold', PLANET_COLORS[activeMaha.planet]?.text ?? 'text-gold')}>
                    {activeMaha.planet}
                  </p>
                  <p className="caption font-mono tabular-nums">{remainingLabel(activeMaha.endDate)}</p>
                </div>
              </div>
              <p className="caption font-mono mt-2">Until {dayjs(activeMaha.endDate).format('MMM YYYY')}</p>
              <div className="mt-3 h-1.5 rounded-full bg-cosmic-dusk overflow-hidden" role="progressbar" aria-valuenow={Math.round(progressPercent(activeMaha))} aria-valuemin={0} aria-valuemax={100}>
                <div
                  className={cn('h-full rounded-full transition-all duration-slow', PLANET_COLORS[activeMaha.planet]?.text.replace('text-', 'bg-') ?? 'bg-gold')}
                  style={{ width: `${progressPercent(activeMaha)}%` }}
                />
              </div>
            </Card>

            {/* Antardasha card */}
            {activeAntar && (
              <Card className={cn('p-5 border', PLANET_COLORS[activeAntar.planet]?.border ?? 'border-cosmic-mist', PLANET_COLORS[activeAntar.planet]?.glow ?? '')}>
                <p className="overline mb-2">Active Antardasha</p>
                <div className="flex items-center gap-3">
                  <span className={cn('text-3xl leading-none', PLANET_COLORS[activeAntar.planet]?.text ?? 'text-violet-glow')} aria-hidden="true">
                    {PLANET_ICONS[activeAntar.planet] ?? ''}
                  </span>
                  <div>
                    <p className={cn('text-lg font-bold', PLANET_COLORS[activeAntar.planet]?.text ?? 'text-violet-glow')}>
                      {activeAntar.planet}
                    </p>
                    <p className="caption font-mono tabular-nums">{remainingLabel(activeAntar.endDate)}</p>
                  </div>
                </div>
                <p className="caption font-mono mt-2">Until {dayjs(activeAntar.endDate).format('MMM YYYY')}</p>
                <div className="mt-3 h-1.5 rounded-full bg-cosmic-dusk overflow-hidden" role="progressbar" aria-valuenow={Math.round(progressPercent(activeAntar))} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className={cn('h-full rounded-full transition-all duration-slow', PLANET_COLORS[activeAntar.planet]?.text.replace('text-', 'bg-') ?? 'bg-violet')}
                    style={{ width: `${progressPercent(activeAntar)}%` }}
                  />
                </div>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Current dasha interpretation */}
      {(current || currentLoading) && (
        <SoftGate locked={!dashaGate.isLoading && !dashaGate.allowed} featureName="Full Dasha Interpretation">
          <Card className="p-5">
            {currentLoading
              ? <div className="flex items-center gap-2 py-4 text-star-dim text-sm"><Clock className="w-4 h-4 animate-spin" aria-hidden /> Loading interpretation…</div>
              : current
                ? <CurrentDashaInterpretation current={current} lang={lang} setLang={setLang} />
                : null
            }
          </Card>
        </SoftGate>
      )}

      {/* Upcoming 12-month transitions */}
      <Card className="p-5" aria-labelledby="transitions-heading">
        <h2 id="transitions-heading" className="overline mb-4">
          Upcoming Transitions — Next 12 Months
        </h2>
        <UpcomingTransitions dashas={dashas} />
      </Card>

      {/* 120-year overview ruler */}
      <Card className="p-5" aria-labelledby="overview-ruler-heading">
        <h2 id="overview-ruler-heading" className="overline mb-3">
          120-Year Overview · {minDate.format('YYYY')} – {maxDate.format('YYYY')}
        </h2>
        <TimelineRuler dashas={dashas} minDate={minDate} totalSpan={totalSpan} />
        <div className="flex justify-between mt-2">
          <span className="caption font-mono">{minDate.format('YYYY')}</span>
          <span className="caption font-mono">{maxDate.format('YYYY')}</span>
        </div>
      </Card>

      {/* Mahadasha list with drill-down */}
      <section aria-labelledby="periods-heading">
        <h2 id="periods-heading" className="overline mb-3">
          Mahadasha Periods — expand for Antardashas → Pratyantardashas
        </h2>
        <div className="space-y-3">
          {dashas.map(node => (
            <MahadashaCard
              key={`${node.planet}-${node.startDate}`}
              node={node}
              planetEnrichment={enrichMap.get(node.planet) ?? null}
            />
          ))}
        </div>
      </section>
    </main>
  )
}
